import { useCallout } from '~/composables/useCallout';
import termsClient from '~/services/termsClient';
import type { ActiveTermResponse, TermCoverage } from '~/services/termsClient.types';

export type TermsScope = { groupId: string; orgUnitId: null } | { groupId: null; orgUnitId: number };

export const currentTermsScope = (): TermsScope | null => {
    const groupId = Spark.state.currentGroup?.group_id ?? null;

    if (groupId) {
        return { groupId, orgUnitId: null };
    }

    const orgUnitId = Spark.state.currentOrgUnit?.id ?? null;

    return orgUnitId ? { groupId: null, orgUnitId } : null;
};

export const termsScopeFor = (groupId?: string, orgUnitId?: number): TermsScope => {
    if (orgUnitId) {
        return { groupId: null, orgUnitId };
    }

    if (!groupId) {
        throw new Error('Terms need either a group or an organizational unit.');
    }

    return { groupId, orgUnitId: null };
};

export const isTermsScopeOwner = (scope: TermsScope): boolean =>
    scope.orgUnitId ? Spark.roleOnCurrentOrgUnit === 'owner' : Spark.roleOnCurrentGroup === 'owner';

export const signingCoverage = (scope: TermsScope): Exclude<TermCoverage, 'corporate_exempt'> =>
    scope.orgUnitId ? 'org_unit' : 'group';

export const fetchActiveTerm = async (teamId: number, scope: TermsScope): Promise<ActiveTermResponse> => {
    if (null === scope.orgUnitId) {
        const { data } = await termsClient.getActiveTerm(teamId, scope.groupId);

        return data;
    }

    const { data } = await termsClient.getOrgUnitActiveTerm(teamId, scope.orgUnitId);

    return {
        term: data.term,
        coverage: data.covered ? 'org_unit' : null,
        accepted: data.covered ? data.accepted : true,
        owner: data.owner,
        org_unit: data.covered ? data.org_unit : null,
        acceptance: data.acceptance,
    };
};

export const acceptActiveTerm = (teamId: number, scope: TermsScope, termId: number) =>
    null === scope.orgUnitId
        ? termsClient.acceptTerm(teamId, scope.groupId, termId)
        : termsClient.acceptOrgUnitTerm(teamId, scope.orgUnitId, termId);

const BILLING = {
    group: {
        section: 'group-billing',
        paymentMethod: '#group-payment-method',
        href: '/settings/group-billing#/billing-details',
    },
    org_unit: {
        section: 'org-unit-billing',
        paymentMethod: '#org-unit-payment-method',
        href: '/settings/org-unit-billing#/billing-details',
    },
};

export const isTermsScopeBillable = (scope: TermsScope): boolean =>
    scope.orgUnitId ? Boolean(Spark.state.currentOrgUnit?.is_billable) : Boolean(Spark.state.currentGroup?.is_billable);

export const redirectToBillingAfterTermsAccepted = (scope: TermsScope) => {
    const callout = useCallout();
    const billing = BILLING[signingCoverage(scope)];

    callout.danger(
        billing.section,
        '<strong>ADDITIONAL ACTION REQUIRED:</strong> Enter billing email, address, and credit card information below. Account may be suspended for missing billing information.',
        {
            ttl: 30,
            action: Spark.stripeKey ? { label: 'Add Payment Method', scrollTo: billing.paymentMethod } : undefined,
        }
    );
    callout.success(billing.section, 'Terms & Conditions successfully accepted!', { ttl: 30 });

    window.location.assign(billing.href);
};
